import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import {
  BehaviorSubject,
  Observable,
  catchError,
  finalize,
  map,
  of,
  shareReplay,
  tap,
} from "rxjs";
import apiEndpoints from "src/app/misc/api-endpoints.misc";

type ConfigMap = Record<string, string>;

@Injectable({
  providedIn: "root",
})
export class ConfigService {
  private readonly cacheStorageKey = "site-configs-cache-v1";

  private readonly configsSubject = new BehaviorSubject<ConfigMap>({});
  readonly configs$ = this.configsSubject.asObservable();

  private inFlightRequest$?: Observable<ConfigMap>;
  private hasLoadedSuccessfully = false;

  constructor(private http: HttpClient) {
    this.restoreConfigsFromCache();
  }

  loadConfigs(force = false): Observable<ConfigMap> {
    const currentConfigs = this.configsSubject.getValue();
    const hasCurrentConfigs = Object.keys(currentConfigs).length > 0;

    if (!force) {
      if (this.hasLoadedSuccessfully && hasCurrentConfigs) {
        return of(currentConfigs);
      }

      if (this.inFlightRequest$) {
        return this.inFlightRequest$;
      }
    }

    this.inFlightRequest$ = this.http.get<unknown>(apiEndpoints.configsSiteUrl).pipe(
      map((payload) => this.normalizeConfigs(payload)),
      tap((configs) => {
        const hasIncomingConfigs = Object.keys(configs).length > 0;
        const shouldUpdateState =
          hasIncomingConfigs ||
          !hasCurrentConfigs ||
          force;

        if (shouldUpdateState) {
          this.configsSubject.next(configs);
          if (hasIncomingConfigs) {
            this.persistConfigsToCache(configs);
          }
        }

        // Only lock the cache flow when we really have usable configs.
        this.hasLoadedSuccessfully = hasIncomingConfigs;
      }),
      catchError((error) => {
        // Keep app usable when config endpoint is temporarily unavailable.
        console.error("Erreur lors du chargement de la config site:", error);
        // Allow next calls to retry when nothing is available yet.
        this.hasLoadedSuccessfully = hasCurrentConfigs;
        return of(currentConfigs);
      }),
      finalize(() => {
        this.inFlightRequest$ = undefined;
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    return this.inFlightRequest$;
  }

  getConfig(key: string): string {
    return this.configsSubject.getValue()[key] || "";
  }

  getAllConfigs(): ConfigMap {
    return this.configsSubject.getValue();
  }

  private normalizeConfigs(data: unknown): ConfigMap {
    if (!data) {
      return {};
    }

    if (Array.isArray(data)) {
      return this.reduceConfigItems(data);
    }

    if (typeof data === "object") {
      const asObject = data as Record<string, unknown>;
      const fromArray =
        this.extractItemsArray(asObject["results"]) ||
        this.extractItemsArray(asObject["items"]) ||
        this.extractItemsArray(asObject["data"]);

      if (fromArray) {
        return this.reduceConfigItems(fromArray);
      }

      if (asObject["results"] && typeof asObject["results"] === "object") {
        return this.normalizeConfigs(asObject["results"]);
      }

      if (asObject["data"] && typeof asObject["data"] === "object") {
        return this.normalizeConfigs(asObject["data"]);
      }

      return Object.entries(asObject).reduce<ConfigMap>((acc, [key, value]) => {
        if (typeof value === "string") {
          acc[key] = value;
          return acc;
        }

        if (value == null) {
          acc[key] = "";
          return acc;
        }

        if (typeof value === "object") {
          const valueObject = value as Record<string, unknown>;
          const rawValue = valueObject["value"] ?? valueObject["meta_value"];

          if (rawValue != null && typeof rawValue !== "object") {
            acc[key] = String(rawValue);
          }
          return acc;
        }

        acc[key] = String(value);
        return acc;
      }, {});
    }

    return {};
  }

  private reduceConfigItems(items: any[]): ConfigMap {
    return items.reduce<ConfigMap>((acc, item: any) => {
      const key = item?.meta_key ?? item?.key ?? item?.name;
      if (typeof key !== "string" || !key) {
        return acc;
      }

      const rawValue = item?.value;
      acc[key] = typeof rawValue === "string" ? rawValue : String(rawValue ?? "");
      return acc;
    }, {});
  }

  private extractItemsArray(value: unknown): any[] | null {
    return Array.isArray(value) ? value : null;
  }

  private restoreConfigsFromCache(): void {
    try {
      const raw = localStorage.getItem(this.cacheStorageKey);
      if (!raw) {
        return;
      }

      const parsed = JSON.parse(raw);
      const normalized = this.normalizeConfigs(parsed);
      if (Object.keys(normalized).length > 0) {
        this.configsSubject.next(normalized);
        this.hasLoadedSuccessfully = true;
      }
    } catch {
      // Ignore invalid cache payload.
    }
  }

  private persistConfigsToCache(configs: ConfigMap): void {
    try {
      localStorage.setItem(this.cacheStorageKey, JSON.stringify(configs));
    } catch {
      // Ignore storage quota/security issues.
    }
  }
}
